<template>
  <div class="service-volumetry-enrich">
    <div class="d-flex align-center flex-wrap mb-4">
      <v-btn
        text
        class="me-2 px-0"
        :to="{ name: 'serviceVolumetry', query: { tab: 'lista' } }"
      >
        <v-icon left>
          {{ icons.mdiArrowLeft }}
        </v-icon>
        Voltar à volumetria
      </v-btn>
    </div>

    <p class="text-2xl mb-1">
      Enriquecer leads
    </p>
    <p class="mb-4 grey--text text--darken-1">
      Selecione a quantidade de leads a reservar a partir de uma volumetria B2C concluída.
      Ao confirmar, a tarifa de Volumetria é debitada do seu saldo conforme a quantidade reservada.
    </p>

    <v-alert
      v-if="!consulta.idFila"
      type="warning"
      dense
      outlined
      class="mb-4"
    >
      Nenhuma volumetria selecionada. Volte à lista e use
      <strong>Enriquecer leads</strong> em um item concluído.
    </v-alert>

    <v-card
      v-else
      outlined
      class="mb-4"
    >
      <v-card-text class="d-flex align-center flex-wrap py-4">
        <div class="flex-grow-1">
          <div class="text-subtitle-1 font-weight-bold mb-1">
            {{ consulta.nome }}
          </div>
          <div class="text-caption grey--text">
            ID fila {{ consulta.idFila }}
            ·
            {{ Number(consulta.volume || 0).toLocaleString('pt-BR') }} registros disponíveis
          </div>
        </div>
        <v-chip
          small
          color="success"
          class="ma-1"
        >
          Concluída
        </v-chip>
      </v-card-text>
    </v-card>

    <v-card
      v-if="consulta.idFila"
      outlined
    >
      <v-card-title class="text-subtitle-1">
        Quantos leads deseja enriquecer?
      </v-card-title>
      <v-card-subtitle>
        Selecione uma quantidade ou digite um valor personalizado
      </v-card-subtitle>
      <v-card-text>
        <v-row dense>
          <v-col
            v-for="q in qtdOptions"
            :key="`q-${q}`"
            cols="6"
            sm="4"
            md="2"
          >
            <v-btn
              block
              large
              :outlined="qtdSelecionada !== q || Boolean(qtdCustom)"
              :color="qtdSelecionada === q && !qtdCustom ? 'primary' : undefined"
              :disabled="q > totalDisponivel || processando"
              class="qty-btn"
              @click="selectQtd(q)"
            >
              <div class="text-center">
                <div class="text-h6 font-weight-bold">
                  {{ q.toLocaleString('pt-BR') }}
                </div>
                <div class="caption grey--text">
                  {{ formatMoney(q * custoUnitario) }}
                </div>
              </div>
            </v-btn>
          </v-col>

          <v-col
            cols="6"
            sm="4"
            md="2"
          >
            <v-btn
              block
              large
              :outlined="!isTudoSelected"
              :color="isTudoSelected ? 'primary' : undefined"
              :disabled="totalDisponivel <= 0 || processando"
              class="qty-btn"
              @click="selectTudo"
            >
              <div class="text-center">
                <div class="text-h6 font-weight-bold">
                  Tudo
                </div>
                <div class="caption grey--text">
                  {{ formatMoney(totalDisponivel * custoUnitario) }}
                </div>
              </div>
            </v-btn>
          </v-col>
        </v-row>

        <div class="d-flex flex-wrap align-center mt-4">
          <v-text-field
            v-model="qtdCustomInput"
            label="Personalizado"
            type="number"
            outlined
            dense
            hide-details
            :min="1"
            :max="totalDisponivel"
            :disabled="processando"
            placeholder="Ex: 750"
            class="custom-qty-field flex-grow-1 me-2 mb-2"
            style="max-width: 280px;"
            @keyup.enter="definirQtdPersonalizada"
          />
          <v-btn
            color="primary"
            class="mb-2"
            :disabled="processando || !canDefinirPersonalizado"
            @click="definirQtdPersonalizada"
          >
            Definir
          </v-btn>
        </div>

        <v-alert
          colored-border
          border="left"
          color="primary"
          class="mt-4 mb-0"
        >
          <div class="d-flex flex-wrap align-center justify-space-between">
            <div>
              <div class="text-overline primary--text mb-1">
                Resumo da operação
              </div>
              <div>
                <strong>{{ qtdFinal.toLocaleString('pt-BR') }} leads</strong>
                × {{ formatMoney(custoUnitario) }} por registro
                <span
                  v-if="carregandoTarifa"
                  class="caption grey--text"
                >(carregando tarifa…)</span>
              </div>
              <div class="caption grey--text mt-1">
                O valor é debitado do seu saldo ao confirmar o pedido.
              </div>
            </div>
            <div class="text-right mt-2 mt-sm-0">
              <div class="caption grey--text">
                Total estimado
              </div>
              <div class="text-h5 font-weight-bold primary--text">
                {{ formatMoney(custoTotal) }}
              </div>
            </div>
          </div>
        </v-alert>

        <v-btn
          block
          color="primary"
          large
          class="mt-4"
          :loading="processando"
          :disabled="!qtdFinal || processando"
          @click="confirmarPedido"
        >
          <v-icon left>
            {{ icons.mdiFlash }}
          </v-icon>
          {{ processando
            ? 'Reservando leads...'
            : `Confirmar pedido de ${qtdFinal.toLocaleString('pt-BR')} leads` }}
        </v-btn>

        <v-progress-linear
          v-if="processando"
          indeterminate
          color="primary"
          class="mt-4"
        />
        <p
          v-if="processando"
          class="text-center caption grey--text mt-2 mb-0"
        >
          Confirmando pedido e débito no saldo…
        </p>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="reavaliacaoDialog"
      max-width="480"
      persistent
    >
      <v-card>
        <v-card-title>
          Quantidade indisponível
        </v-card-title>
        <v-card-text>
          <p class="mb-2">
            {{ reavaliacao.mensagem || 'A quantidade solicitada não está mais disponível.' }}
          </p>
          <p class="mb-0">
            Disponível agora:
            <strong>{{ Number(reavaliacao.qtdDisponivel || 0).toLocaleString('pt-BR') }}</strong>
            (solicitado:
            {{ Number(reavaliacao.qtdSolicitada || 0).toLocaleString('pt-BR') }})
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="reavaliacaoDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="processando"
            @click="aceitarReavaliacao"
          >
            Aceitar disponível
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card
      v-if="reservaResult"
      outlined
      class="mt-4"
    >
      <v-card-title class="success--text">
        Pedido confirmado
      </v-card-title>
      <v-card-text>
        <v-alert
          type="success"
          dense
          text
          class="mb-4"
        >
          {{ reservaResult.mensagem || 'Leads reservados com sucesso.' }}
        </v-alert>
        <v-alert
          v-if="csvStatus === 'processando'"
          type="info"
          dense
          text
          class="mb-4"
        >
          Gerando arquivo CSV da extração…
        </v-alert>
        <v-alert
          v-else-if="csvStatus === 'concluido'"
          type="success"
          dense
          text
          class="mb-4"
        >
          CSV pronto para download.
        </v-alert>
        <v-alert
          v-else-if="csvStatus === 'erro'"
          type="error"
          dense
          text
          class="mb-4"
        >
          {{ csvErro || 'Falha ao gerar o CSV dos leads.' }}
          Se a extração ficou com Status=erro, reserve novamente (nova quantidade) —
          o download só libera após CSV concluído.
        </v-alert>
        <v-row dense>
          <v-col
            cols="12"
            sm="4"
          >
            <div class="caption grey--text text-uppercase">
              Extração
            </div>
            <div class="text-h6 font-weight-bold">
              #{{ reservaResult.idExtracao || '—' }}
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="4"
          >
            <div class="caption grey--text text-uppercase">
              Leads reservados
            </div>
            <div class="text-h6 font-weight-bold">
              {{ Number(reservaResult.qtdReservada || qtdFinal).toLocaleString('pt-BR') }}
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="4"
          >
            <div class="caption grey--text text-uppercase">
              Debitado Ligdata
            </div>
            <div class="text-h6 font-weight-bold">
              {{ formatMoney(reservaResult.ligdataValor || reservaResult.valorTotal || custoTotal) }}
            </div>
          </v-col>
        </v-row>
        <p
          v-if="reservaResult.ligdataCampaignId"
          class="text-caption grey--text mb-0 mt-2"
        >
          Cobrança Ligdata registrada (campaign #{{ reservaResult.ligdataCampaignId }}).
        </p>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-btn
          color="primary"
          :loading="baixando"
          :disabled="!reservaResult.idExtracao"
          @click="baixarCsv"
        >
          <v-icon left>
            {{ icons.mdiDownload }}
          </v-icon>
          Baixar CSV
        </v-btn>
        <v-btn
          text
          :to="{ name: 'serviceVolumetry', query: { tab: 'lista' } }"
        >
          Voltar à lista
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import {
  mdiArrowLeft,
  mdiDownload,
  mdiFlash,
} from '@mdi/js'
import {
  downloadExtracaoLeadsVolumetria,
  gerarCsvLeadsVolumetria,
  gerarLeadsVolumetria,
  getTarifaLeadsVolumetria,
} from '@/services/volumetryB2c'

const QTD_OPTIONS = [100, 500, 1000, 5000]
const CUSTO_UNITARIO_FALLBACK = 0

function pickNum(src, ...keys) {
  if (!src || typeof src !== 'object') return 0
  for (const key of keys) {
    if (src[key] != null && src[key] !== '') {
      const n = Number(src[key])
      if (Number.isFinite(n)) return n
    }
  }
  return 0
}

function pickStr(src, ...keys) {
  if (!src || typeof src !== 'object') return ''
  for (const key of keys) {
    if (src[key] != null && String(src[key]).trim() !== '') {
      return String(src[key])
    }
  }
  return ''
}

function normalizeGerarCsvResponse(data) {
  const raw = data || {}
  const status = String(raw.Status ?? raw.status ?? '')
    .toLowerCase()
    .trim()
  return {
    status,
    mensagem: pickStr(raw, 'Mensagem', 'mensagem'),
    idExtracao: pickStr(raw, 'IdExtracao', 'idExtracao'),
    isConcluido: status === 'concluido',
  }
}

async function messageFromDownloadError(error) {
  const data = error?.response?.data
  if (data instanceof Blob) {
    try {
      const text = await data.text()
      const json = JSON.parse(text)
      return json?.message || json?.erro || json?.error || text
    } catch {
      return 'Não foi possível baixar o CSV.'
    }
  }
  return data?.message
    || data?.erro
    || error?.message
    || 'Não foi possível baixar o CSV.'
}

function normalizeGerarLeadsResponse(data) {
  const raw = data || {}
  const status = String(raw.Status ?? raw.status ?? '')
    .toLowerCase()
    .trim()

  const ligdata = raw.ligdataDebito && typeof raw.ligdataDebito === 'object'
    ? raw.ligdataDebito
    : null

  const base = {
    status,
    mensagem: pickStr(raw, 'Mensagem', 'mensagem'),
    idExtracao: pickStr(raw, 'IdExtracao', 'idExtracao'),
    qtdReservada: pickNum(raw, 'QtdReservada', 'qtdReservada'),
    valorTotal: pickNum(raw, 'ValorTotal', 'valorTotal'),
    qtdDisponivel: pickNum(raw, 'QtdDisponivel', 'qtdDisponivel'),
    qtdSolicitada: pickNum(raw, 'QtdSolicitada', 'qtdSolicitada'),
    ligdataValor: ligdata ? pickNum(ligdata, 'value') : 0,
    ligdataCampaignId: ligdata ? pickStr(ligdata, 'campaignId') : '',
  }

  if (status === 'aguardando_reavaliacao') {
    return { ...base, needsReavaliacao: true }
  }

  return {
    ...base,
    needsReavaliacao: false,
    isReservado: status === 'reservado',
  }
}

function filenameFromContentDisposition(header) {
  if (!header) return null
  const match = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(String(header))
  if (!match) return null
  try {
    return decodeURIComponent(match[1].replace(/"/g, '').trim())
  } catch {
    return match[1].replace(/"/g, '').trim()
  }
}

export default {
  name: 'ServiceVolumetryEnrich',

  data() {
    const q = this.$route.query || {}
    return {
      icons: { mdiArrowLeft, mdiDownload, mdiFlash },
      qtdOptions: QTD_OPTIONS,
      custoUnitario: CUSTO_UNITARIO_FALLBACK,
      carregandoTarifa: false,
      tarifaErro: '',
      qtdSelecionada: 100,
      qtdCustom: '',
      qtdCustomInput: '',
      processando: false,
      baixando: false,
      csvStatus: '',
      csvErro: '',
      reservaResult: null,
      reavaliacaoDialog: false,
      reavaliacao: {},
      consulta: {
        idFila: q.idFila || q.volumetria_id || '',
        nome: q.nome || 'Volumetria',
        volume: Number(q.volume || 0) || 0,
      },
    }
  },

  async mounted() {
    await this.loadTarifaLigdata()
  },

  computed: {
    totalDisponivel() {
      return Math.max(0, Number(this.consulta.volume) || 0)
    },
    isTudoSelected() {
      return !this.qtdCustom
        && this.totalDisponivel > 0
        && this.qtdSelecionada === this.totalDisponivel
    },
    qtdFinal() {
      if (this.qtdCustom) {
        const n = Number(this.qtdCustom)
        if (!Number.isFinite(n) || n <= 0) return 0
        return Math.min(Math.floor(n), this.totalDisponivel || n)
      }
      return Number(this.qtdSelecionada) || 0
    },
    custoTotal() {
      return this.qtdFinal * this.custoUnitario
    },
    canDefinirPersonalizado() {
      const n = Number(this.qtdCustomInput)
      return Number.isFinite(n) && n > 0
    },
  },

  methods: {
    formatMoney(value) {
      return Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },

    async loadTarifaLigdata() {
      this.carregandoTarifa = true
      this.tarifaErro = ''
      try {
        const data = await getTarifaLeadsVolumetria()
        const unitary = Number(data?.unitaryValue)
        if (!Number.isFinite(unitary) || unitary < 0) {
          throw new Error('Tarifa Volumetria inválida.')
        }
        this.custoUnitario = unitary
      } catch (error) {
        this.custoUnitario = CUSTO_UNITARIO_FALLBACK
        this.tarifaErro = error?.response?.data?.message
          || error?.message
          || 'Não foi possível carregar a tarifa Ligdata (service 35).'
        this.$notify(this.tarifaErro, 'error')
      } finally {
        this.carregandoTarifa = false
      }
    },

    selectQtd(q) {
      this.qtdSelecionada = q
      this.qtdCustom = ''
      this.qtdCustomInput = ''
    },

    selectTudo() {
      this.qtdSelecionada = this.totalDisponivel
      this.qtdCustom = ''
      this.qtdCustomInput = ''
    },

    definirQtdPersonalizada() {
      const n = Number(this.qtdCustomInput)
      if (!Number.isFinite(n) || n <= 0) {
        this.$notify('Informe uma quantidade válida.', 'warning')
        return
      }
      const max = this.totalDisponivel
      const applied = max > 0 ? Math.min(Math.floor(n), max) : Math.floor(n)
      if (max > 0 && n > max) {
        this.$notify(
          `Quantidade ajustada para o máximo disponível (${max.toLocaleString('pt-BR')}).`,
          'info',
        )
      }
      this.qtdCustom = String(applied)
      this.qtdCustomInput = String(applied)
      this.qtdSelecionada = 0
    },

    async confirmarPedido() {
      await this.submitGerarLeads(this.qtdFinal, 0)
    },

    async aceitarReavaliacao() {
      const qtd = this.reavaliacao.qtdDisponivel || this.qtdFinal
      this.reavaliacaoDialog = false
      await this.submitGerarLeads(qtd, 1)
    },

    async submitGerarLeads(qtd, origemReenvio = 0) {
      if (!this.consulta.idFila || !qtd) {
        this.$notify('Informe uma quantidade válida.', 'warning')
        return
      }

      this.processando = true
      this.reservaResult = null
      this.csvStatus = ''
      this.csvErro = ''
      try {
        const raw = await gerarLeadsVolumetria({
          idFilaVolumetria: Number(this.consulta.idFila),
          qtdSolicitada: Number(qtd),
          origemReenvio: origemReenvio ? 1 : 0,
        })
        const normalized = normalizeGerarLeadsResponse(raw)

        // Mantém custoUnitario da tarifa Ligdata (service 35).

        if (normalized.needsReavaliacao) {
          this.reavaliacao = normalized
          this.reavaliacaoDialog = true
          return
        }

        if (normalized.isReservado) {
          this.reservaResult = normalized
          const debitMsg = normalized.ligdataValor
            ? ` Leads reservados. Debitado ${this.formatMoney(normalized.ligdataValor)} do saldo Ligdata.`
            : 'Leads reservados com sucesso.'
          this.$notify(debitMsg.trim(), 'success')
          if (normalized.idExtracao) {
            void this.gerarCsvBackground(normalized.idExtracao)
          }
          return
        }

        this.$notify(
          normalized.mensagem || 'Resposta inesperada ao gerar leads.',
          'error',
        )
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.message
          || 'Não foi possível reservar os leads.'
        this.$notify(message, 'error')
      } finally {
        this.processando = false
      }
    },

    async gerarCsvBackground(idExtracao, { force = false } = {}) {
      // Só gera CSV com Status=reservada. Depois de concluido, re-chamar quebra.
      if (!force && this.csvStatus === 'concluido') {
        return true
      }
      if (!force && this.csvStatus === 'processando') {
        return false
      }

      this.csvStatus = 'processando'
      this.csvErro = ''
      try {
        const raw = await gerarCsvLeadsVolumetria({
          idExtracao: Number(idExtracao),
          delimitador: ';',
        })
        const normalized = normalizeGerarCsvResponse(raw)
        if (normalized.isConcluido) {
          this.csvStatus = 'concluido'
          return true
        }
        this.csvStatus = 'erro'
        this.csvErro = normalized.mensagem
          || 'CSV ainda não concluído. Tente novamente em instantes.'
        return false
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.message
          || 'Falha ao gerar CSV.'
        // Se já estava concluído, a 2ª chamada falha com "nao esta reservada"
        // — nesse caso o arquivo existe e o download deve seguir.
        const jaConcluido = /nao esta reservada|não está reservada|nao existe/i.test(
          String(message),
        )
        if (jaConcluido) {
          this.csvStatus = 'concluido'
          this.csvErro = ''
          return true
        }
        this.csvStatus = 'erro'
        this.csvErro = message
        console.warn('[volumetria] gerar-csv-leads', error?.response?.data || message)
        return false
      }
    },

    async baixarCsv() {
      const idExtracao = this.reservaResult?.idExtracao
      if (!idExtracao) return

      this.baixando = true
      try {
        // Só gera se ainda não concluiu
        if (this.csvStatus !== 'concluido') {
          const ok = await this.gerarCsvBackground(idExtracao)
          if (!ok) {
            this.$notify(
              this.csvErro || 'Não foi possível gerar o CSV. Tente novamente ou reserve outra quantidade.',
              'error',
            )
            return
          }
        }

        const response = await downloadExtracaoLeadsVolumetria(idExtracao)
        const blob = response.data
        if (blob instanceof Blob && blob.type && blob.type.includes('application/json')) {
          const text = await blob.text()
          let message = text
          try {
            const json = JSON.parse(text)
            message = json?.message || json?.erro || text
          } catch { /* ignore */ }
          throw Object.assign(new Error(message), { response: { data: { message } } })
        }
        const disposition = response.headers?.['content-disposition']
        const filename = filenameFromContentDisposition(disposition)
          || `leads_extracao_${idExtracao}.csv`

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        this.$notify('Download iniciado.', 'success')
      } catch (error) {
        const message = await messageFromDownloadError(error)
        this.csvStatus = 'erro'
        this.csvErro = message
        this.$notify(message, 'error')
      } finally {
        this.baixando = false
      }
    },
  },
}
</script>

<style scoped>
.qty-btn {
  text-transform: none !important;
  min-height: 104px !important;
  height: auto !important;
  padding: 16px 12px !important;
}

.qty-btn ::v-deep .v-btn__content {
  padding: 0;
}

.qty-btn .text-h6 {
  line-height: 1.3;
  margin-bottom: 4px;
}

.qty-btn .caption {
  line-height: 1.3;
}
</style>
